import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
} from "@mui/material";
import { StaticDatePicker, StaticDateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersShortcutsItem } from "@mui/x-date-pickers/PickersShortcuts";
import { addDays, isPast, isToday, setHours, setMinutes, startOfDay } from "date-fns";
import { sv } from "date-fns/locale";
import { ReactNode, useEffect, useState } from "react";

export const DEFAULT_DATE_SHORTCUTS: PickersShortcutsItem<Date>[] = [
	{ label: "Tomorrow", getValue: () => addDays(startOfDay(new Date()), 1) },
	{ label: "In 2 days", getValue: () => addDays(startOfDay(new Date()), 2) },
	{ label: "In 1 week", getValue: () => addDays(startOfDay(new Date()), 7) },
	{ label: "In 2 weeks", getValue: () => addDays(startOfDay(new Date()), 14) },
	{ label: "In 1 month", getValue: () => addDays(startOfDay(new Date()), 30) },
	{ label: "In 3 months", getValue: () => addDays(startOfDay(new Date()), 90) },
];

export const COOLDOWN_SHORTCUTS: PickersShortcutsItem<Date>[] = [
	{ label: "Tomorrow", getValue: () => addDays(startOfDay(new Date()), 1) },
	{ label: "In 1 week", getValue: () => addDays(startOfDay(new Date()), 7) },
	{ label: "In 2 weeks", getValue: () => addDays(startOfDay(new Date()), 14) },
	{ label: "In 1 month", getValue: () => addDays(startOfDay(new Date()), 30) },
	{ label: "In 3 months", getValue: () => addDays(startOfDay(new Date()), 90) },
	{ label: "In 6 months", getValue: () => addDays(startOfDay(new Date()), 180) },
];

export const shouldDisablePastDate = (date: Date): boolean => {
	return !isToday(date) && isPast(date);
};

export const getDefaultFutureDate = (daysFromNow: number = 7): Date => {
	return addDays(startOfDay(new Date()), daysFromNow);
};

const toMidnight = (base: Date): Date => setMinutes(setHours(base, 0), 0);

type DatePickerDialogProps = {
	open: boolean;
	onClose: () => void;
	onSave: (date: Date, time: Date | null) => void;
	title: string;
	saveButtonText?: string;
	initialDate?: Date;
	initialTime?: Date | null;
	shortcuts?: PickersShortcutsItem<Date>[];
	extraContent?: ReactNode;
	dataComponent: string;
	withTime?: boolean;
};

export const DatePickerDialog = ({
	open,
	onClose,
	onSave,
	title,
	saveButtonText = "Save",
	initialDate,
	initialTime,
	shortcuts,
	extraContent,
	dataComponent,
	withTime = false,
}: DatePickerDialogProps) => {
	const buildInitial = (): Date => {
		const baseDate = initialDate ?? getDefaultFutureDate();
		if (!withTime) return baseDate;
		if (initialTime) {
			return setMinutes(
				setHours(baseDate, initialTime.getHours()),
				initialTime.getMinutes()
			);
		}
		return toMidnight(baseDate);
	};

	const [selectedValue, setSelectedValue] = useState<Date>(buildInitial());

	// Reset to initial value each time the dialog opens so stale state from a
	// previous interaction (e.g. a 14:30 pick) doesn't leak into the next open.
	useEffect(() => {
		if (open) {
			setSelectedValue(buildInitial());
		}
	}, [open]);

	const handleChange = (newValue: Date | null) => {
		if (newValue instanceof Date) {
			setSelectedValue(newValue);
		}
	};

	const handleSave = () => {
		onSave(selectedValue, withTime ? selectedValue : null);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
			<Dialog
				open={open}
				onClose={onClose}
				maxWidth={withTime ? "md" : "sm"}
				data-component={dataComponent}
			>
				<DialogTitle>{title}</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						{withTime ? (
							<StaticDateTimePicker
								value={selectedValue}
								onChange={handleChange}
								ampm={false}
								orientation="landscape"
								shouldDisableDate={shouldDisablePastDate}
								sx={{
									"& .MuiMultiSectionDigitalClockSection-root": {
										overflowY: "scroll",
										scrollbarGutter: "stable",
									},
								}}
								slotProps={{
									...(shortcuts
										? { shortcuts: { items: shortcuts } }
										: {}),
									actionBar: { actions: [] },
								}}
							/>
						) : (
							<StaticDatePicker
								value={selectedValue}
								onChange={handleChange}
								shouldDisableDate={shouldDisablePastDate}
								slotProps={{
									...(shortcuts
										? { shortcuts: { items: shortcuts } }
										: {}),
									actionBar: { actions: [] },
								}}
							/>
						)}
						{extraContent}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>Cancel</Button>
					<Button variant="contained" onClick={handleSave}>
						{saveButtonText}
					</Button>
				</DialogActions>
			</Dialog>
		</LocalizationProvider>
	);
};

type UseDatePickerDialogReturn = {
	isOpen: boolean;
	open: (initialDate?: Date) => void;
	close: () => void;
	selectedDate: Date;
};

export const useDatePickerDialog = (
	defaultDaysFromNow: number = 7
): UseDatePickerDialogReturn => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedDate, setSelectedDate] = useState<Date>(
		getDefaultFutureDate(defaultDaysFromNow)
	);

	const open = (initialDate?: Date) => {
		setSelectedDate(initialDate ?? getDefaultFutureDate(defaultDaysFromNow));
		setIsOpen(true);
	};

	const close = () => {
		setIsOpen(false);
	};

	return { isOpen, open, close, selectedDate };
};
